import type { AxisScale } from '@visx/axis';
import type { ScaleInput } from '@visx/scale';
import { bisectLeft, bisector, range as d3Range } from '@visx/vendor/d3-array';

// @TODO make more robust to null/undefined scaled values
/** Finds the nearest datum in a single direction (x or y) closest to the specified `scaledValue`. */
export function findNearestDatumSingleDimension<Scale extends AxisScale, Datum extends object>({
  scale,
  accessor,
  scaledValue,
  data,
}: {
  scale: Scale;
  accessor: (d: Datum) => ScaleInput<Scale>;
  scaledValue: number;
  data: Datum[];
}) {
  let nearestDatum: Datum;
  let nearestDatumIndex: number;
  // if scale has .invert(), convert svg coord to nearest data value
  if ('invert' in scale && typeof scale.invert === 'function') {
    const bisect = bisector(accessor).left;
    // find closest data value, then map that to closest datum
    const dataValue = Number(scale.invert(scaledValue));
    const index = bisect(data, dataValue);
    // take the two datum nearest this index, and compute which is closer
    const nearestDatum0 = data[index - 1];
    const nearestDatum1 = data[index];
    nearestDatum =
      !nearestDatum0 || Math.abs(dataValue - accessor(nearestDatum0)) > Math.abs(dataValue - accessor(nearestDatum1))
        ? nearestDatum1
        : nearestDatum0;
    nearestDatumIndex = nearestDatum === nearestDatum0 ? index - 1 : index;
  } else if ('step' in scale && typeof scale.step !== 'undefined') {
    // band scales don't have an invert function but they do have discrete domains
    // so we manually invert
    const domain = scale.domain();
    const range = scale.range().map(Number);
    const sortedRange = [...range].sort((a, b) => a - b); // bisectLeft assumes sort
    const rangePoints = d3Range(sortedRange[0], sortedRange[1], scale.step());
    const domainIndex = bisectLeft(rangePoints, scaledValue);
    // y-axis scales may have reverse ranges, correct for this
    const sortedDomain = range[0] < range[1] ? domain : domain.reverse();
    const domainValue = sortedDomain[domainIndex - 1];
    const index = data.findIndex((d) => String(accessor(d)) === String(domainValue));
    nearestDatum = data[index];
    nearestDatumIndex = index;
  } else {
    return null;
  }

  const nearestDatumIsNotDefined = nearestDatum === null || nearestDatum === undefined;
  const nearestDatumIndexIsNotDefined = nearestDatumIndex === null || nearestDatumIndex === undefined;
  if (nearestDatumIsNotDefined || nearestDatumIndexIsNotDefined) {
    return null;
  }

  const distance = Math.abs(Number(scale(accessor(nearestDatum))) - scaledValue);

  return { datum: nearestDatum, index: nearestDatumIndex, distance };
}
